import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import ScannerAssistant from "../components/ScannerAssistant";
import countriesData from "../data/countries.json";
import {
  extractInternationalNumber,
  splitCountryAndNumber,
} from "../utils/phoneUtils";
import {
  Globe,
  Phone,
  Gift,
  Plus,
  Trash2,
  ArrowRight,
  CheckCircle,
  Smartphone,
  Building2,
  ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LoadingButton from "../components/LoadingButton";
import LanguageSelector from "../components/LanguageSelector";

export default function Onboarding() {
  const { user, updateUserData } = useAuth();
  const [phones, setPhones] = useState([]);
  // El nombre de la empresa ya no se pide en el registro (21-9-2026): se pide
  // aquí, donde ya se piden los números, y hasta entonces vale el del usuario.
  const [companyName, setCompanyName] = useState("");
  const [saving, setSaving] = useState(false);
  const [phoneConflict, setPhoneConflict] = useState(null); // { phone, existing_company_id }
  const [transferCode, setTransferCode] = useState("");
  const [transferStatus, setTransferStatus] = useState(null);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [step, setStep] = useState(1); // 1 = phones, 2 = create scanner
  const [scanners, setScanners] = useState([]);
  const [creatingScanner, setCreatingScanner] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Skip Step 1 (Phones) for Free Plan or if user already has phones configured.
  // Solo una vez (1-10-2026): sin la guarda, el botón «Volver» del paso 2 no hacía nada, porque este
  // efecto volvía a empujar al paso 2 en cuanto se pedía el paso 1.
  const autoSkipped = useRef(false);
  useEffect(() => {
    if (!user || autoSkipped.current) return;

    // Si el usuario es free plan, saltar al step 2
    if ((user.subscription_plan || "free") === "free" && step === 1) {
      autoSkipped.current = true;
      setStep(2);
      return;
    }

    // Si el usuario ya tiene phones configurados y no ha completado el onboarding,
    // significa que se quedó en el step de crear escáner
    if (
      user.phones &&
      user.phones.length > 0 &&
      !user.onboarding_completed &&
      step === 1
    ) {
      autoSkipped.current = true;
      setStep(2);
    }
  }, [user, step]);

  const handleLanguageChange = async (newLang) => {
    try {
      // Update i18n immediately
      i18n.changeLanguage(newLang);

      // Save to backend
      const response = await api.updateUser({
        language: newLang,
      });

      // Update local user state
      const updatedUser = response.user || response;
      updateUserData(updatedUser);
    } catch (error) {
      console.error(
        "Error saving language preference during onboarding:",
        error
      );
    }
  };

  // Get language from user/company config (already set in App.js, but ensure it's applied)
  const lang = user?.language || i18n.language || "en";

  useEffect(() => {
    if (user) {
      const rawPhones = user?.phones || [];
      let parsedPhonesRaw = rawPhones;
      if (typeof rawPhones === "string") {
        try {
          parsedPhonesRaw = JSON.parse(rawPhones);
        } catch (err) {
          const matched = (rawPhones.match(/\+\d{6,15}/g) || []).map((m) => m);
          parsedPhonesRaw = matched.length ? matched : [rawPhones];
        }
      }
      const parsedPhones = (
        Array.isArray(parsedPhonesRaw) ? parsedPhonesRaw : [parsedPhonesRaw]
      ).map((p) => {
        if (!p) return { country: user?.country || "ES", number: "" };
        // Handle DynamoDB typed attribute object: { S: "+341234" }
        if (
          typeof p === "object" &&
          Object.prototype.hasOwnProperty.call(p, "S")
        ) {
          p = p.S;
        }
        if (typeof p === "object") {
          return {
            country: p.country || user?.country || "ES",
            number: p.number || "",
          };
        }
        // Normalize phone and extract country + number
        const extracted = extractInternationalNumber(p);
        if (extracted)
          return splitCountryAndNumber(
            extracted,
            countriesData,
            user?.country || "ES"
          );
        return {
          country: user?.country || "ES",
          number: String(p).replace(/[^0-9]/g, ""),
        };
      });
      setPhones(
        parsedPhones.length
          ? parsedPhones
          : [{ country: user?.country || "ES", number: "" }]
      );
    }
    // fetch scanners to know if we should prompt to create one
    (async () => {
      try {
        const items = await api.listScanners();
        setScanners(items || []);
      } catch (err) {
        // ignore
      }
    })();
  }, [user, i18n]);

  const addPhone = () => setPhones([...phones, { country: "ES", number: "" }]);
  const stripDialCode = (val) => {
    if (!val) return "";
    // Normalize leading 00 to + and remove spaces
    let s = String(val).trim().replace(/^00/, "+").replace(/\s+/g, "");

    // Only strip if it starts with + (explicit international format)
    if (!s.startsWith("+")) {
      return s.replace(/\D/g, "");
    }

    // keep only digits and +
    s = s.replace(/[^+0-9]/g, "");
    // If it doesn't contain + or has no digits, return only digits
    const digitsOnly = s.replace(/\D/g, "");
    if (!digitsOnly) return "";
    // Try to match known country dial codes
    const match = countriesData
      .slice()
      .sort((a, b) => b.dial_code.length - a.dial_code.length)
      .find((c) => digitsOnly.startsWith(c.dial_code.replace("+", "")));
    if (match) {
      const dial = match.dial_code.replace("+", "");
      return digitsOnly.slice(dial.length);
    }
    return digitsOnly;
  };

  const updatePhone = (idx, key, value) =>
    setPhones(
      phones.map((p, i) => {
        if (i !== idx) return p;
        if (key === "country") {
          // When user changes country, remove any existing leading dial code from the number
          return { ...p, country: value, number: stripDialCode(p.number) };
        }
        if (key === "number") {
          // Sanitize number: remove leading dial code if user pasted full international number
          return { ...p, number: stripDialCode(value) };
        }
        return { ...p, [key]: value };
      })
    );
  const removePhone = (idx) => {
    // Don't allow removing if it's the only phone or if it's the user's primary phone
    if (phones.length <= 1) return;
    setPhones(phones.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      setPhoneConflict(null);
      setTransferStatus(null);
      const validPhones = phones
        .map((p) => {
          if (!p) return null;
          const countryObj = countriesData.find((c) => c.code === p.country);
          const dial = countryObj?.dial_code || "+34";
          const clean = String(p.number || "").replace(/[^0-9]/g, "");
          return clean ? `${dial}${clean}` : null;
        })
        .filter(Boolean);
      const patch = {
        phones: validPhones,
        language: i18n.language,
      };
      if (companyName.trim()) patch.company_name = companyName.trim();
      // If there are scanners, complete onboarding
      if (scanners && scanners.length > 0) {
        patch.onboarding_completed = true;
      }
      const response = await api.updateUser(patch);
      const updatedUser = response.user || response;

      // If there are no scanners, go to create scanner step
      if (!scanners || scanners.length === 0) {
        updateUserData({ ...user, ...updatedUser });
        setStep(2);
        // Scroll to bottom so the scanner form is visible
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
        return;
      }

      // Otherwise go to the first scanner (instead of dashboard) so user can test upload/whatsapp immediately.
      if (scanners && scanners.length > 0 && scanners[0]?.id) {
        navigate(`/scanners/${scanners[0].id}`);
        // Update context after navigation to avoid OnboardingRoute redirecting to dashboard
        setTimeout(() => {
          updateUserData({ ...user, ...updatedUser });
        }, 100);
      } else {
        // If no scanners, stay on onboarding to create one
        updateUserData({ ...user, ...updatedUser });
        setStep(2);
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }
    } catch (error) {
      const apiData = error?.response?.data;
      if (
        error?.response?.status === 409 &&
        apiData?.error_code === "PHONE_IN_USE"
      ) {
        setPhoneConflict({
          phone: apiData.phone,
          existing_company_id: apiData.existing_company_id,
        });
        setTransferCode("");
        setSendingCode(true);
        try {
          await api.requestPhoneTransferCode({ phone: apiData.phone });
          setTransferStatus(
            t(
              "onboarding.codeSent",
              "Code sent to that number's WhatsApp and to its current owner's email. Enter it below to confirm."
            )
          );
        } catch (e) {
          setTransferStatus(
            t(
              "onboarding.codeSendError",
              "Could not send the code. Please try again."
            )
          );
        } finally {
          setSendingCode(false);
        }
      } else {
        console.error("Error saving onboarding:", error);
      }
    } finally {
      setSaving(false);
    }
  };

  const requestTransferCode = async () => {
    if (!phoneConflict?.phone) return;
    setSendingCode(true);
    setTransferStatus(null);
    try {
      await api.requestPhoneTransferCode({ phone: phoneConflict.phone });
      setTransferStatus(
        t(
          "onboarding.codeSent",
          "Code sent to that number's WhatsApp and to its current owner's email. Enter it below to confirm."
        )
      );
    } catch (err) {
      setTransferStatus(
        t(
          "onboarding.codeSendError",
          "Could not send the code. Please try again."
        )
      );
    } finally {
      setSendingCode(false);
    }
  };

  const verifyTransferCode = async () => {
    if (!phoneConflict?.phone) return;
    const code = String(transferCode || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setTransferStatus(t("onboarding.codeInvalid", "Enter a 6-digit code."));
      return;
    }
    setVerifyingCode(true);
    setTransferStatus(null);
    try {
      await api.verifyPhoneTransferCode({ phone: phoneConflict.phone, code });
      setTransferStatus(t("onboarding.transferOk", "Verified. Retrying..."));
      setTransferCode("");
      setPhoneConflict(null);
      await save();
    } catch (err) {
      const apiData = err?.response?.data;
      const msg =
        i18n.language === "es"
          ? apiData?.message || "Código incorrecto o expirado."
          : apiData?.message_en ||
            apiData?.message ||
            "Incorrect or expired code.";
      setTransferStatus(msg);
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleCreateScanner = async (payload) => {
    setCreatingScanner(true);
    try {
      const response = await api.createScanner(payload);
      // Remote API may return different shapes (scanner object, { scanner: {...} } or { data: ... }).
      const scannerId =
        response?.id ||
        response?.scanner?.id ||
        response?.data?.id ||
        response?.data?.scanner?.id;

      // Mark onboarding complete
      await api.updateUser({
        onboarding_completed: true,
        language: i18n.language,
      });

      // Navegamos primero con { state: { fromOnboarding: true } } para que DashboardRoute
      // permita el acceso aunque onboarding_completed sea aún false en el contexto local.
      // Si actualizáramos el contexto ANTES del navigate, OnboardingRoute capturaría el
      // re-render con onboarding_completed=true y haría un <Navigate to="/" /> sobreescribiendo
      // nuestra navegación destino.
      if (scannerId) {
        navigate(`/scanners/${scannerId}`, { state: { fromOnboarding: true } });
      } else {
        navigate(`/scanners`, { state: { fromOnboarding: true } });
      }

      // Actualizamos el contexto local tras la navegación
      setTimeout(() => {
        updateUserData({ ...user, onboarding_completed: true });
      }, 100);
    } catch (err) {
      console.error("Failed to create scanner during onboarding:", err);
      throw err;
    } finally {
      setCreatingScanner(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gray-50 py-4 sm:py-8 md:py-12 px-3 sm:px-4 md:px-6">
      {/* Background Blobs */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary-200/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-200/30 rounded-full blur-[120px]" />
      </div>

      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className="w-full max-w-3xl relative z-10"
      >
        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="bg-white/80 backdrop-blur-xl border border-white/60 p-4 sm:p-6 md:p-8 lg:p-10 rounded-xl sm:rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            >
              <div className="text-center mb-6 sm:mb-8 md:mb-10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 w-auto h-16 sm:h-20 mx-auto mb-4 sm:mb-6 shadow-inner border border-white flex items-center justify-center"
                >
                  <img
                    src="/WhappScan.png"
                    alt="WhappScan"
                    className="h-8 sm:h-10 w-auto object-contain"
                  />
                </motion.div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-2 sm:mb-3 px-2">
                  {t("onboarding.welcome_title")}
                </h1>
                <p className="text-gray-500 text-sm sm:text-base md:text-lg font-medium px-2">
                  {t("onboarding.welcome_message")}
                </p>
              </div>

              {/* Language Selection */}
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xs sm:text-sm uppercase tracking-wider text-gray-500 font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <Globe className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>{t("onboarding.select_language")}</span>
                </h2>
                <LanguageSelector
                  value={user?.language || i18n.language}
                  onChange={handleLanguageChange}
                />
              </div>

              {/* Nombre de la empresa */}
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xs sm:text-sm uppercase tracking-wider text-gray-500 font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <Building2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>{t("onboarding.company_title")}</span>
                </h2>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={t("onboarding.company_placeholder")}
                  className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-gray-900 font-semibold placeholder-gray-400"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {t("onboarding.company_hint")}
                </p>
              </div>

              {/* Phone Numbers */}
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xs sm:text-sm uppercase tracking-wider text-gray-500 font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <Smartphone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>{t("onboarding.phones_title")}</span>
                </h2>

                {/* Info Box */}
                <div className="bg-blue-50/50 border border-blue-100 rounded-lg sm:rounded-xl p-4 sm:p-5 mb-4 sm:mb-6 flex gap-3 sm:gap-4">
                  <div className="bg-blue-100/50 p-2 rounded-lg h-fit text-blue-600 flex-shrink-0">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-blue-900 font-bold text-xs sm:text-sm mb-1">
                      {t("onboarding.phones_explanation_title")}
                    </h3>
                    <p className="text-blue-800/80 text-xs sm:text-sm leading-relaxed">
                      {t("onboarding.phones_explanation")}
                    </p>
                  </div>
                </div>

                {phoneConflict && (
                  <div className="bg-primary-50/50 border border-primary-100 rounded-lg sm:rounded-xl p-4 sm:p-5 mb-4 sm:mb-6">
                    <div className="font-bold text-primary-900 mb-1 text-sm sm:text-base">
                      {t(
                        "onboarding.phoneConflictTitle",
                        "Phone already in use"
                      )}
                    </div>
                    <div className="text-xs sm:text-sm text-primary-900/80 mb-3">
                      {transferStatus ||
                        t(
                          "onboarding.phoneConflictHelp",
                          "Verify the phone with the 6-digit code to transfer it to your organization."
                        )}
                    </div>
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      <button
                        type="button"
                        onClick={requestTransferCode}
                        disabled={sendingCode}
                        className={
                          sendingCode
                            ? "px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-bold bg-gray-100 text-gray-500 text-sm sm:text-base"
                            : "px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-bold bg-white border border-primary-200 hover:bg-primary-50 text-sm sm:text-base transition-colors"
                        }
                      >
                        {sendingCode
                          ? t("onboarding.sending", "Sending...")
                          : t("onboarding.sendCode", "Send code")}
                      </button>
                      <input
                        value={transferCode}
                        onChange={(e) => setTransferCode(e.target.value)}
                        inputMode="numeric"
                        pattern="\\d{6}"
                        maxLength={6}
                        placeholder={t(
                          "onboarding.codePlaceholder",
                          "6-digit code"
                        )}
                        className="flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl border border-primary-200 bg-white focus:ring-2 focus:ring-primary-200 outline-none text-sm sm:text-base"
                      />
                      <button
                        type="button"
                        onClick={verifyTransferCode}
                        disabled={verifyingCode}
                        className={
                          verifyingCode
                            ? "px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-bold bg-gray-100 text-gray-500 text-sm sm:text-base"
                            : "px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-bold bg-gray-900 hover:bg-black text-white text-sm sm:text-base transition-colors"
                        }
                      >
                        {verifyingCode
                          ? t("onboarding.verifying", "Verifying...")
                          : t("onboarding.verify", "Verify")}
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-gray-700 break-all">
                      {t("onboarding.conflictPhone", "Phone")}:{" "}
                      {phoneConflict.phone}
                    </div>
                  </div>
                )}

                <div className="space-y-3 sm:space-y-4">
                  {phones.map((p, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex flex-row gap-2 sm:gap-3"
                    >
                      <div className="w-24 sm:w-32 flex-shrink-0 flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden border border-gray-200 focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-500 transition-all bg-white/50">
                        <div className="relative w-full">
                          <select
                            value={p.country}
                            onChange={(e) =>
                              updatePhone(i, "country", e.target.value)
                            }
                            className="w-full bg-transparent border-none py-2.5 sm:py-3 pl-3 pr-8 text-xs sm:text-sm font-bold text-gray-700 focus:ring-0 cursor-pointer appearance-none"
                          >
                            {countriesData.map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.code}
                              </option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden border border-gray-200 focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-500 transition-all bg-white/50">
                        <div className="flex items-center px-3 bg-gray-50/50 text-gray-600 font-bold border-r border-gray-200 text-xs sm:text-sm min-w-[60px] sm:min-w-[75px] justify-center">
                          {countriesData.find((c) => c.code === p.country)
                            ?.dial_code || "+34"}
                        </div>
                        <input
                          type="tel"
                          value={p.number}
                          onChange={(e) =>
                            updatePhone(i, "number", e.target.value)
                          }
                          className="w-full border-none py-2.5 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm text-gray-900 font-semibold placeholder-gray-400 focus:ring-0 bg-transparent"
                          placeholder={t("onboarding.phone_placeholder")}
                        />
                        {phones.length > 1 && (
                          <button
                            onClick={() => removePhone(i)}
                            className="px-3 sm:px-4 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0 border-l border-gray-100"
                            aria-label={t(
                              "onboarding.remove_phone",
                              "Remove phone"
                            )}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}

                  {phones.length <
                  (user?.max_team_members === -1
                    ? Infinity
                    : user?.max_team_members || 1) ? (
                    <button
                      onClick={addPhone}
                      className="text-primary-600 font-bold text-xs sm:text-sm flex items-center gap-2 hover:text-primary-700 transition-colors px-1 py-2"
                    >
                      <Plus className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span>{t("onboarding.add_phone")}</span>
                    </button>
                  ) : (
                    <div className="flex flex-col items-start px-1 py-2">
                      <p className="text-amber-600 text-xs sm:text-sm font-medium">
                        {t("onboarding.phone_limit_reached", {
                          limit:
                            user?.max_team_members === -1 ||
                            user?.max_team_members === null
                              ? "∞"
                              : user?.max_team_members || 1,
                        }) ||
                          `Has alcanzado el límite de ${
                            user?.max_team_members === -1 ||
                            user?.max_team_members === null
                              ? "∞"
                              : user?.max_team_members || 1
                          } números para tu plan.`}
                      </p>
                      <Link
                        to="/pricing"
                        className="text-primary-600 hover:text-primary-700 font-bold text-xs underline transition-colors"
                      >
                        {t("onboarding.upgrade_for_more") ||
                          "Mejora tu plan para añadir más miembros"}
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Tokens Bonus */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-orange-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 relative overflow-hidden group mb-6 sm:mb-8">
                <div className="absolute top-0 right-0 p-2 sm:p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Gift className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 text-orange-500 transform rotate-12" />
                </div>
                <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 md:gap-5">
                  <div className="bg-white p-2 sm:p-3 rounded-lg sm:rounded-xl shadow-sm text-orange-500 shadow-orange-100 flex-shrink-0">
                    <Gift className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-orange-900 font-bold text-base sm:text-lg mb-1">
                      {t("onboarding.free_tokens_title")}
                    </h3>
                    <p className="text-orange-800/80 text-xs sm:text-sm leading-relaxed">
                      {t("onboarding.free_tokens_message")}
                    </p>
                  </div>
                  <div className="ml-auto sm:ml-0 text-right sm:text-left flex-shrink-0">
                    <span className="block text-2xl sm:text-3xl md:text-4xl font-black text-orange-500 tracking-tight">
                      20
                    </span>
                    <span className="text-xs font-bold text-orange-400 uppercase tracking-wider">
                      tokens
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-3 sm:pt-4 border-t border-gray-100">
                <LoadingButton
                  onClick={save}
                  loading={saving}
                  disabled={
                    saving || phones.filter((p) => p.number).length === 0
                  }
                  className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base shadow-lg shadow-primary-600/20 hover:shadow-xl hover:shadow-primary-600/30 transition-all duration-300 transform hover:-translate-y-0.5 w-full sm:w-auto"
                >
                  <span className="flex items-center justify-center gap-2">
                    {scanners && scanners.length === 0
                      ? t("onboarding.continue_create_scanner") ||
                        "Continue & Create Scanner"
                      : t("onboarding.continue")}
                    {!saving && (
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </span>
                </LoadingButton>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="bg-white/80 backdrop-blur-xl border border-white/60 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-xl"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                    {t("onboarding.create_scanner_title") ||
                      "Create your first Scanner"}
                  </h2>
                  <p className="text-gray-500 mt-1 text-sm sm:text-base">
                    {t(
                      "onboarding.create_scanner_subtitle",
                      "Connect your device to start scanning"
                    )}
                  </p>
                </div>
                {(user?.subscription_plan || "free") !== "free" && (
                  <button
                    onClick={() => setStep(1)}
                    className="text-xs sm:text-sm font-bold text-gray-500 hover:text-gray-900 px-3 sm:px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors self-start sm:self-auto"
                  >
                    {t("common.back") || "Back"}
                  </button>
                )}
              </div>

              <ScannerAssistant
                onSave={handleCreateScanner}
                creating={creatingScanner}
                language={lang}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
